/**
 * VisitSyncAdmin Component
 * 
 * Admin configuration panel for Visit Synchronization & Compliance rules.
 * Allows admins to view and configure compliance requirements by discipline.
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import {
  Settings, Shield, Clock, Users, Stethoscope, Heart,
  Activity, CheckCircle, AlertTriangle, FileText, Info,
  RefreshCw, Loader2, AlertCircle
} from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const DISCIPLINE_ICONS = {
  rn: Stethoscope,
  sw: Users,
  chaplain: Heart,
  cna: Activity
};

const DISCIPLINE_COLORS = {
  rn: "text-blue-500 bg-blue-500/10",
  sw: "text-pink-500 bg-pink-500/10",
  chaplain: "text-purple-500 bg-purple-500/10",
  cna: "text-green-500 bg-green-500/10"
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("hospice_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function VisitSyncAdmin({ isDark = true }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDiscipline, setSelectedDiscipline] = useState("rn");

  // Fetch admin config
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/visit-sync/admin/config`,
        { headers: getAuthHeaders() }
      );
      setConfig(response.data);
    } catch (err) {
      console.error("Failed to fetch config:", err);
      toast.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
        <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Failed to load configuration</p>
        <Button variant="outline" onClick={fetchConfig} className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const selectedRules = config.compliance_rules?.[selectedDiscipline];
  const DisciplineIcon = DISCIPLINE_ICONS[selectedDiscipline] || Stethoscope;
  const disciplineColor = DISCIPLINE_COLORS[selectedDiscipline] || DISCIPLINE_COLORS.rn;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-teal-500/20' : 'bg-teal-100'}`}>
                <Settings className="w-6 h-6 text-teal-500" />
              </div>
              <div>
                <CardTitle className={isDark ? 'text-white' : 'text-gray-900'}>
                  Visit Sync Configuration
                </CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                  CMS hospice compliance rules and visit sync settings
                </CardDescription>
              </div>
            </div>
            <Button variant="outline" onClick={fetchConfig} className={isDark ? 'border-slate-600' : ''}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Time Tolerance</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {config.time_tolerance_minutes} min
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-blue-500" />
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Disciplines</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {config.disciplines?.length || 0}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-violet-500" />
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Doc Types</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {config.documentation_types?.length || 0}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-emerald-500" />
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Hard Stops</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {config.hard_stop_enabled ? "Enabled" : "Disabled"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compliance Rules by Discipline */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Shield className="w-5 h-5 text-teal-500" />
            CMS Compliance Rules by Discipline
          </CardTitle>
          <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            Required and optional documentation elements for each discipline
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
            <TabsList className={`w-full ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
              {Object.keys(config.compliance_rules || {}).map((discipline) => {
                const Icon = DISCIPLINE_ICONS[discipline] || Stethoscope;
                const rules = config.compliance_rules[discipline];
                return (
                  <TabsTrigger key={discipline} value={discipline} className="flex-1">
                    <Icon className="w-4 h-4 mr-2" />
                    {rules?.name?.split(" ")[0] || discipline.toUpperCase()}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {Object.keys(config.compliance_rules || {}).map((discipline) => {
              const rules = config.compliance_rules[discipline];
              const Icon = DISCIPLINE_ICONS[discipline] || Stethoscope;
              const color = DISCIPLINE_COLORS[discipline] || DISCIPLINE_COLORS.rn;
              
              return (
                <TabsContent key={discipline} value={discipline} className="mt-4">
                  <div className="space-y-6">
                    {/* Discipline Header */}
                    <div className={`flex items-center gap-3 p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
                      <div className={`p-2 rounded-lg ${color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {rules?.name}
                        </h3>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {rules?.required_elements?.length} required • {rules?.optional_elements?.length || 0} optional elements
                        </p>
                      </div>
                    </div>

                    {/* Required Elements */}
                    <div>
                      <Label className={`text-sm font-semibold flex items-center gap-2 mb-3 ${
                        isDark ? 'text-slate-300' : 'text-gray-700'
                      }`}>
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        Required Elements ({rules?.required_elements?.length})
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {rules?.required_elements?.map((elem, idx) => {
                          const isHardStop = rules?.hard_stop_on_missing?.includes(elem.key);
                          return (
                            <div 
                              key={idx}
                              className={`p-3 rounded-lg border ${
                                isHardStop
                                  ? isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
                                  : isDark ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {elem.label}
                                  </p>
                                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                    {elem.description}
                                  </p>
                                </div>
                                {isHardStop && (
                                  <Badge variant="destructive" className="text-xs flex-shrink-0">
                                    Hard Stop
                                  </Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <Separator className={isDark ? 'bg-slate-700' : 'bg-gray-200'} />

                    {/* Optional Elements */}
                    {rules?.optional_elements?.length > 0 && (
                      <div>
                        <Label className={`text-sm font-semibold flex items-center gap-2 mb-3 ${
                          isDark ? 'text-slate-300' : 'text-gray-700'
                        }`}>
                          <Info className="w-4 h-4 text-blue-500" />
                          Optional Elements ({rules?.optional_elements?.length})
                        </Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {rules?.optional_elements?.map((elem, idx) => (
                            <div 
                              key={idx}
                              className={`p-3 rounded-lg border ${
                                isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'
                              }`}
                            >
                              <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {elem.label}
                              </p>
                              <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                {elem.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hard Stops Info */}
                    {rules?.hard_stop_on_missing?.length > 0 && (
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                          <div>
                            <p className={`font-medium text-sm ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
                              Hard Stop Requirements
                            </p>
                            <p className={`text-xs mt-1 ${isDark ? 'text-amber-300/80' : 'text-amber-700'}`}>
                              Visit cannot be finalized without: {rules.hard_stop_on_missing.map(k => 
                                rules.required_elements?.find(e => e.key === k)?.label || k
                              ).join(", ")}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </CardContent>
      </Card>

      {/* Settings */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Settings className="w-5 h-5 text-slate-400" />
            Global Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className={isDark ? 'text-white' : 'text-gray-900'}>Hard Stop Enforcement</Label>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Prevent finalization when required elements are missing
                </p>
              </div>
              <Switch checked={config.hard_stop_enabled} disabled />
            </div>
            <Separator className={isDark ? 'bg-slate-700' : 'bg-gray-200'} />
            <div className="flex items-center justify-between">
              <div>
                <Label className={isDark ? 'text-white' : 'text-gray-900'}>Auto-Consolidate</Label>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Automatically merge documentation into unified visit notes
                </p>
              </div>
              <Switch checked={config.auto_consolidate} disabled />
            </div>
            <Separator className={isDark ? 'bg-slate-700' : 'bg-gray-200'} />
            <div className="flex items-center justify-between">
              <div>
                <Label className={isDark ? 'text-white' : 'text-gray-900'}>Time Window Tolerance</Label>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Minutes between documentation entries to group in same visit
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input 
                  type="number" 
                  value={config.time_tolerance_minutes} 
                  disabled
                  className={`w-20 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
                />
                <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>min</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
